import { useState } from 'react'
import { useRecoilValue } from 'recoil'
import { useSnackbar } from 'notistack'

import {
  Button,
  Dialog,
  FormControlLabel,
  Grid,
  Paper,
  Switch,
  TextField,
} from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

import MaintenanceEdit from '../component/MaintenanceEdit'
import { maintenanceState } from '../stores/MaintenanceAtom'
import { LoginUserAtom } from '../stores/LoginUserAtom'

import '../css/style.css'

function MaintenanceSetting() {
  const maintenance = useRecoilValue(maintenanceState)
  const loginUserData = useRecoilValue(LoginUserAtom)

  const { enqueueSnackbar } = useSnackbar()

  const formatTime = (value: unknown): string => {
    if (typeof value !== 'string' || value.trim() === '') {
      return ''
    }

    const parts = value.split(':')

    if (parts.length < 2) {
      return value
    }

    return `${parts[0]}:${parts[1]}`
  }

  const formatDate = (value: Date): string => {
    const year = value.getFullYear()
    const month = String(value.getMonth() + 1).padStart(2, '0')
    const date = String(value.getDate()).padStart(2, '0')

    return `${year}-${month}-${date}`
  }

  /*
   * YYYY-MM-DDをnew Date("YYYY-MM-DD")で生成すると、
   * UTC扱いによる日付ずれが起きる可能性があるため、
   * 年月日を分解してローカル日時として生成する。
   */
  const parseDate = (value: unknown): Date | null => {
    if (typeof value !== 'string' || value.trim() === '') {
      return null
    }

    const parts = value.split('-').map(Number)

    if (parts.length !== 3) {
      return null
    }

    const [year, month, date] = parts

    if (
      !Number.isInteger(year) ||
      !Number.isInteger(month) ||
      !Number.isInteger(date)
    ) {
      return null
    }

    const parsedDate = new Date(year, month - 1, date)

    if (Number.isNaN(parsedDate.getTime())) {
      return null
    }

    return parsedDate
  }

  const initialDisplay = Number(maintenance?.display) === 1
  const initialFlag = Number(maintenance?.flag) === 1

  const initialStartTime = formatTime(maintenance?.start_time)
  const initialEndTime = formatTime(maintenance?.end_time)

  const initialDay =
    typeof maintenance?.day === 'string'
      ? maintenance.day
      : ''

  const [start, setStart] = useState<string>(initialStartTime)
  const [end, setEnd] = useState<string>(initialEndTime)
  const [day, setDay] = useState<string>(initialDay)
  const [display, setDisplay] = useState<boolean>(initialDisplay)
  const [flag, setFlag] = useState<boolean>(initialFlag)
  const [editOpen, setEditOpen] = useState(false)

  const displayChecked = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDisplay(event.target.checked)
  }

  const flagChecked = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = event.target.checked

    setFlag(checked)

    if (checked) {
      enqueueSnackbar(
        'メンテナンスモードをONにしました。（保存後に実行されます）',
        {
          variant: 'info',
          autoHideDuration: 5000,
        }
      )
    } else {
      setDisplay(false)

      enqueueSnackbar(
        'メンテナンスモードをOFFにしました。（保存後に実行されます）',
        {
          variant: 'info',
          autoHideDuration: 5000,
        }
      )
    }
  }

  const handleDateChange = (value: Date | null) => {
    if (value === null || Number.isNaN(value.getTime())) {
      setDay('')
      return
    }

    setDay(formatDate(value))
  }

  const validateInput = (): boolean => {
    if (!day) {
      enqueueSnackbar('メンテナンス予定日を入力してください。', {
        variant: 'warning',
      })

      return false
    }

    if (!start) {
      enqueueSnackbar('開始時刻を入力してください。', {
        variant: 'warning',
      })

      return false
    }

    if (!end) {
      enqueueSnackbar('終了時刻を入力してください。', {
        variant: 'warning',
      })

      return false
    }

    if (start >= end) {
      enqueueSnackbar(
        '終了時刻は開始時刻より後の時刻を指定してください。',
        {
          variant: 'warning',
        }
      )

      return false
    }

    return true
  }

  const handleConfirm = () => {
    if (!validateInput()) {
      return
    }

    setEditOpen(true)
  }

  const maintenanceData = {
    access_token: loginUserData?.accessToken ?? '',
    algorithm: loginUserData?.algorithm ?? '',
    flag: 'maintenanceEdit',
    start,
    end,
    day,
    maintenanceFlag: flag,
    display,
  }

  return (
    <>
      <h3>メンテナンスモード</h3>

      <Paper sx={{ m: 1 }}>
        <Grid container>
          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{ textAlign: 'center', p: 1 }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={display}
                  onChange={displayChecked}
                />
              }
              label="予定表示"
            />
          </Grid>

          <Grid
            size={{ xs: 12, sm: 6 }}
            sx={{ textAlign: 'center', p: 1 }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={flag}
                  onChange={flagChecked}
                />
              }
              label="メンテナンス実行"
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4 }} sx={{ p: 1 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="メンテナンス予定日"
                value={parseDate(day)}
                onChange={handleDateChange}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid
            size={{ xs: 12, sm: 4 }}
            sx={{ textAlign: 'center', p: 1 }}
          >
            <TextField
              label="開始時刻"
              type="time"
              value={start}
              onChange={(event) => {
                setStart(event.target.value)
              }}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>

          <Grid
            size={{ xs: 12, sm: 4 }}
            sx={{ textAlign: 'center', p: 1 }}
          >
            <TextField
              label="終了時刻"
              type="time"
              value={end}
              onChange={(event) => {
                setEnd(event.target.value)
              }}
              fullWidth
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />
          </Grid>

          <Grid
            size={{ xs: 12 }}
            sx={{ textAlign: 'center', p: 1 }}
          >
            <Button
              variant="contained"
              sx={{ width: '100%' }}
              onClick={handleConfirm}
            >
              確認
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        scroll="paper"
        fullWidth
        aria-labelledby="scroll-dialog-title"
        aria-describedby="scroll-dialog-description"
      >
        <MaintenanceEdit
          data={maintenanceData}
          setEditOpen={setEditOpen}
        />
      </Dialog>
    </>
  )
}

export default MaintenanceSetting